import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Transition } from 'react-transition-group';
import { AlertPopupContainer } from '../alert-popup/alertPopup.styled';
import Button from '../button';
import Heading from '../typography';

// 1. Improved Naming and Strong Typing
interface ConfirmationModalProps {
  isShow: boolean;
  title: string;
  handleClose: () => void;
  handleSubmit?: () => void; // Replaced 'any' with a specific function signature
  loading?: boolean;
}

const ConfirmationModal = ({
  isShow,
  handleClose,
  handleSubmit,
  loading = false, // Default value for cleaner logic
  title,
}: ConfirmationModalProps): JSX.Element | null => {
  // 2. Keyboard Accessibility: Close on Escape key
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleClose();
    }
  }, [handleClose]);

  useEffect(() => {
    if (isShow) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isShow, handleKeyDown]);

  // 3. Extracted JSX for Portaling
  const modalNode = (
    <Transition
      in={isShow}
      timeout={250}
      unmountOnExit // Cleans up DOM when hidden
      appear
    >
      {(state) => (
        <AlertPopupContainer>
          {/* Backdrop: Clicking it closes the modal */}
          <div
            className={`modalBackdrop modalBackdrop-${state}`}
            onClick={handleClose}
            aria-hidden="true"
          >
            <div
              className={`sideBar sideBar-${state}`}
              onClick={(e) => e.stopPropagation()} // Prevents closing when clicking modal content
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-title"
            >
              <div className='popupDialog'>
                <div className='popupContent'>
                  <Heading
                    id="modal-title"
                    variant='h5'
                    className='mb-5'
                    title={title}
                  />

                  <div className='popupButtons'>
                    <Button
                      variant='primary-dark'
                      title='Close'
                      onClick={handleClose}
                    />

                    {/* Only render Confirm button if handleSubmit is provided */}
                    {handleSubmit && (
                      <Button
                        variant='primary'
                        title='Confirm'
                        onClick={handleSubmit}
                        icon={
                          <span className="material-symbols-outlined">
                            check_circle
                          </span>
                        }
                        loading={loading}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AlertPopupContainer>
      )}
    </Transition>
  );

  // 4. Portaling to document.body to avoid z-index/overflow issues
  return typeof document !== 'undefined'
    ? createPortal(modalNode, document.body)
    : null;
};

export default ConfirmationModal;